<script lang="ts">
    import { OTP_COOLDOWN, OTP_VERIFICATION_DELAY, PREVIEW_MODE } from '$lib/global/constants';

    import { otpApi } from '$lib/global/otp/api';
    import { OtpError } from '$lib/global/otp/otp.types';
    import { getTrackingService } from '$lib/global/tracking/TrackingServiceInstance';

    import { onDestroy, onMount } from 'svelte';

    import messages from './messages.json';
    import OtpVerificationModal from './OtpVerificationModal.View.svelte';

    import type { Language } from '$lib/types/Language';
    import type { Theme } from '$lib/types/Theme';
    import type { CloseModalFn } from 'svelte-promise-modals';

    export let type: 'phone' | 'email';
    export let value: string;
    export let closeModal: CloseModalFn<boolean>;
    export let theme: Theme;
    export let language: Language;
    export let campaignId: string;
    export let campaignVersionId: string;

    let coolDown = OTP_COOLDOWN;
    let isSendAgainDisabled = true;
    let tickInterval: ReturnType<typeof setInterval>;
    let verificationIntentId: string;
    let errorMessage: string | null = null;
    let isLoading = false;
    let isVerified = false;
    let previewMessage: string | null = null;

    const { sessionId: perspectiveSessionId, userId: globalId } = getTrackingService();

    const createOtpIntent = async () => {
        if (PREVIEW_MODE) {
            previewMessage =
                language === 'de'
                    ? 'Gib einen beliebigen Code ein. Diese Nachricht erscheint nur in der Vorschau.'
                    : "Type in any code. This message won't appear in the published funnel.";
            return;
        }

        try {
            const payloadBase = {
                device: { id: globalId },
                session: { id: perspectiveSessionId },
                campaign: { id: campaignId },
                campaignVersion: { id: campaignVersionId },
            };

            const payload =
                type === 'phone'
                    ? { ...payloadBase, phone: value }
                    : { ...payloadBase, email: value };

            verificationIntentId = await otpApi.createOtpIntent(payload);
        } catch (error) {
            console.warn('Creating OTP intent failed', { error });

            if (error instanceof OtpError) {
                errorMessage = (messages[language] as { errors: { [key: string]: string } }).errors[
                    error.type
                ];
            } else {
                errorMessage = 'Something went wrong. Please try again.';
            }
        }
    };

    const resetOtp = () => {
        isVerified = false;
        isLoading = false;
        errorMessage = null;
        previewMessage = null;
        coolDown = OTP_COOLDOWN;
        isSendAgainDisabled = true;
    };

    const onVerify = async (code: string) => {
        if (PREVIEW_MODE) {
            isVerified = true;

            return new Promise((resolve) => {
                setTimeout(() => {
                    resolve(closeModal(true));
                }, OTP_VERIFICATION_DELAY);
            });
        }

        isLoading = true;

        try {
            const result = await otpApi.verifyOtp({
                otp: code,
                verificationIntentId,
            });

            if (result === true) {
                isVerified = true;
                return new Promise((resolve) => {
                    setTimeout(() => {
                        resolve(closeModal(true));
                    }, OTP_VERIFICATION_DELAY);
                });
            }
        } catch (error) {
            console.warn('Verifying OTP failed', { error });

            if (error instanceof OtpError) {
                errorMessage = (messages[language] as { errors: { [key: string]: string } }).errors[
                    error.type
                ];
            } else {
                errorMessage = 'Something went wrong. Please try again.';
            }
        } finally {
            isLoading = false;
        }
    };

    const onSendAgain = () => {
        resetOtp();
        createOtpIntent();
    };

    onMount(() => {
        createOtpIntent();
    });

    onDestroy(() => {
        clearInterval(tickInterval);
    });
</script>

<svelte:component
    this={OtpVerificationModal}
    {closeModal}
    {isSendAgainDisabled}
    {value}
    {type}
    {onVerify}
    {onSendAgain}
    {coolDown}
    {theme}
    {errorMessage}
    {isLoading}
    {isVerified}
    {language}
    {previewMessage}
/>

<style>
    :global(.backdrop) {
        z-index: 1000 !important;
        background-color: rgba(0, 0, 0, 0.2);
    }
</style>
